/**
 * Hero variant resolver — picks personalized headline/subheadline/CTA copy
 * based on inbound ad attribution (UTM params + landing-page path).
 *
 * Why we do this:
 *   Quality Score in Google Ads heavily rewards ad↔landing-page relevance.
 *   When someone searches "shipbob alternative" and the hero literally says
 *   "Switching from ShipBob? You'll save ~30%", Google ranks the ad higher
 *   and the prospect feels less like they were dumped on a generic page.
 *
 * Resolution order (first match wins):
 *   1. utm_content explicit variant key (highest precedence — manual override)
 *   2. utm_term contains a competitor keyword (e.g. "shipbob alternative")
 *   3. utm_campaign contains a known theme (e.g. "switching", "shopify")
 *   4. utm_term / utm_campaign contains a platform name (shopify, tiktok, etsy)
 *   5. Default fallback copy
 *
 * All matching is case-insensitive and substring-based.
 */

import { getAttribution } from './analytics';

/**
 * Read UTM params from the current URL. We read from the URL directly (not
 * from the localStorage attribution record) because:
 *
 *   1. The attribution record is written inside App's useEffect, which runs
 *      AFTER the Hero component mounts. Reading from localStorage on first
 *      paint would always miss the current visit's UTMs.
 *   2. localStorage stays sticky across visits, so a returning visitor would
 *      see the variant tied to their FIRST ad click instead of whichever ad
 *      they clicked today. That's the literal bug Zack reported.
 *
 * URLSearchParams handles `+`-as-space and percent-decoding for us, so an
 * ad URL like `?utm_term=shipbob+alternative` is parsed correctly.
 */
function readUtmFromUrl() {
  if (typeof window === 'undefined') return {};
  try {
    const params = new URLSearchParams(window.location.search);
    return {
      utm_term: params.get('utm_term') || '',
      utm_campaign: params.get('utm_campaign') || '',
      utm_content: params.get('utm_content') || '',
    };
  } catch {
    return {};
  }
}

// Default copy is what every non-ad visitor sees. Identical to the hardcoded
// hero before personalization was wired.
const DEFAULT_VARIANT = {
  key: 'default',
  preheadline: null,
  headline: 'The 3PL Industry Needed Disruption.',
  highlight: 'We Delivered.',
  subheadline: 'Transparent pricing. No minimums. No contracts.',
  callout: '$2 per order. That\u2019s it.',
  cta: 'Get Started',
  ctaHref: '#contact',
};

// Variant pool. Order doesn't matter except for utm_content overrides.
const VARIANTS = {
  shipbob: {
    key: 'shipbob',
    preheadline: 'Switching from ShipBob?',
    headline: 'Smaller fees.',
    highlight: 'Bigger margins.',
    subheadline: 'Brands typically save 20\u201330% switching from ShipBob to Shipmaster. No minimums, no contracts, transparent $2-per-order pricing.',
    callout: '$2 per order. ShipBob averages $4\u20136.',
    cta: 'Compare to ShipBob',
    // CTA text promises a comparison; route to the actual compare page,
    // not the Calendly section.
    ctaHref: '/compare/shipbob',
  },
  shipmonk: {
    key: 'shipmonk',
    preheadline: 'Tired of ShipMonk surprises?',
    headline: 'Flat pricing.',
    highlight: 'Zero hidden fees.',
    subheadline: 'Predictable $2-per-order pricing, no contracts, and a real human you can reach. See the side-by-side with ShipMonk.',
    callout: '$2 per order. That\u2019s the entire pick & pack.',
    cta: 'Compare to ShipMonk',
    ctaHref: '/compare/shipmonk',
  },
  shopify: {
    key: 'shopify',
    preheadline: 'Built for Shopify brands',
    headline: 'Shopify-native fulfillment.',
    highlight: '$2 per order.',
    subheadline: 'Pick, pack, and ship Shopify orders the day they come in. One-click integration, real-time tracking, no minimums.',
    callout: 'Same-day cutoff. $2 per order. No contracts.',
    cta: 'Get Started',
    ctaHref: '#contact',
  },
  tiktok: {
    key: 'tiktok',
    preheadline: 'TikTok Shop fulfillment',
    headline: 'Keep up with the algorithm.',
    highlight: '$2 per order.',
    subheadline: 'Same-day shipping from South Florida so your viral product actually arrives before the trend dies. Built for TikTok Shop merchants.',
    callout: 'Same-day shipping. $2 per order. No contracts.',
    cta: 'Get Started',
    ctaHref: '#contact',
  },
  amazon: {
    key: 'amazon',
    preheadline: 'Amazon FBM done right',
    headline: 'Stay seller-rating safe.',
    highlight: '$2 per order.',
    subheadline: 'Same-day FBM fulfillment with carrier-compliant labels and tracking that protects your Amazon seller metrics.',
    callout: '$2 per order. No minimums. No FBM surprises.',
    cta: 'Get Started',
    ctaHref: '#contact',
  },
  switching: {
    key: 'switching',
    preheadline: 'Switching 3PLs?',
    headline: 'We\u2019ll match your current rate.',
    highlight: 'Or beat it.',
    subheadline: 'Send us your current 3PL invoice. We\u2019ll come back in 24 hours with a side-by-side that beats it on price, transit, or both.',
    callout: '$2 per order. Migration handled for you.',
    cta: 'Get My Beat-Your-Rate Quote',
    ctaHref: '#contact',
  },
};

// Substring matchers checked against utm_term / utm_campaign (lowercased).
const KEYWORD_RULES = [
  { match: 'shipbob', variant: 'shipbob' },
  { match: 'shipmonk', variant: 'shipmonk' },
  { match: 'switching', variant: 'switching' },
  { match: 'alternative', variant: 'switching' },
  { match: 'tiktok', variant: 'tiktok' },
  { match: 'shopify', variant: 'shopify' },
  { match: 'amazon', variant: 'amazon' },
  { match: 'fbm', variant: 'amazon' },
];

export function resolveHeroVariant() {
  if (typeof window === 'undefined') return DEFAULT_VARIANT;

  // Prefer the CURRENT URL's UTMs over the stored attribution record.
  // Only fall back to stored attribution if the URL has none — that keeps
  // the personalized hero working for users who land on /, then click a
  // sub-page, then bounce back to / (the second / load has no UTMs).
  const fromUrl = readUtmFromUrl();
  const hasUrlUtm = fromUrl.utm_term || fromUrl.utm_campaign || fromUrl.utm_content;
  const source = hasUrlUtm ? fromUrl : getAttribution();

  const term = (source.utm_term || '').toLowerCase();
  const campaign = (source.utm_campaign || '').toLowerCase();
  const content = (source.utm_content || '').toLowerCase();

  // 1. Explicit override via utm_content=<exact variant key>. Highest
  //    precedence so we can hard-wire a specific ad to a specific hero.
  if (content && VARIANTS[content]) return VARIANTS[content];

  // 2. Keyword-based substring match across utm_term, utm_campaign, and
  //    utm_content. Including utm_content here means ads tagged like
  //    `utm_content=shipbob_ad_v2` still land on the ShipBob hero.
  const haystack = `${term} ${campaign} ${content}`;
  for (const rule of KEYWORD_RULES) {
    if (haystack.includes(rule.match)) return VARIANTS[rule.variant];
  }
  return DEFAULT_VARIANT;
}
